/**
 * Builds/updates query params in a path string.
 * Updates the Nao parameter for pagination and removes it for the first page.
 * Removes CI-5 parameter to prevent unnecessary bot crawling on pagination links beyond the first page.
 */
export const updateParams = (path, pageSize, pageNum = 1) => {
  const [basePath, queryString = ''] = path.split('?');

  /**
   * Treat page numbers <= 1 as first page preventing negative offsets and
   * ensuring the Nao parameter is only included for valid pagination links
   */
  const isFirstPage = pageNum <= 1;
  const offset = pageSize * (pageNum - 1);

  // Use URLSearchParams for proper query string manipulation
  const params = new URLSearchParams(queryString);

  // Update or remove Nao pagination parameter
  if (isFirstPage) {
    params.delete('Nao');
  } else {
    // Remove CI-5 parameter to prevent bot crawling on pagination links beyond the first page
    params.delete('CI-5');
    params.set('Nao', offset);
  }

  // URLSearchParams adds '=' for valueless params
  const updatedQueryString = params.toString().replace(/=(?=&|$)/g, '');
  return `${basePath}${updatedQueryString ? `?${updatedQueryString}` : ''}`;
};

/**
 * Determines whether a pagination item should have an href attribute.
 * Excludes hrefs for last page buttons, selected page, and non-consecutive last page numbers.
 */
export const shouldIncludeHref = (item, page, count) => {
  const currentPage = page ?? 1;
  const isLastPage = item.page === count;
  const isLastPageConsecutive = isLastPage && currentPage === count - 1;

  if (item.type === 'last') {
    // Mobile >> button: never linked so the last page is not emphasized in importance
    return false;
  }
  if (item.type === 'page' && item.selected) {
    // Current page number should not be linked
    return false;
  }
  if (item.type === 'page' && isLastPage && !isLastPageConsecutive) {
    // Last page number should not be linked unless it is consecutive to the current page
    return false;
  }

  // This returns a boolean if we are dealing with a valid and in-range page
  // Ex: if 'next' or 'previous' button aren't in range, they won't get an href
  return item.page >= 1 && item.page <= count;
};
